import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/features/auth/store/auth.store';
import type { ApiError, RefreshTokenResponse } from '@/shared/types';
import { getBackendLanguage } from '@/shared/i18n';
import { router } from '@/app/routes';
import { ROUTES } from '@/app/routes/routes.config';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

/**
 * Decode JWT exp claim using browser built-in atob — no external dependency.
 * Returns the expiry time in ms, or Infinity if the token can't be decoded.
 */
function getTokenExpiryMs(token: string): number {
  try {
    const base64Payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64Payload));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : Infinity;
  } catch {
    return Infinity; // Can't decode — don't block the request
  }
}

// Refresh the access token 60 s before it expires to avoid 401 round-trips
const PROACTIVE_THRESHOLD_MS = 60_000;
let proactiveRefreshPromise: Promise<void> | null = null;

/**
 * Subscribe to token refresh
 */
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers when token is refreshed
 */
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Request interceptor - Proactive token refresh + auth token injection
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { tokens, setTokens } = useAuthStore.getState();

    // Proactive refresh: if the access token expires within the threshold,
    // renew it before sending the request to avoid a 401 round-trip.
    // The httpOnly cookie carries the refreshToken — no need to check it here.
    if (
      tokens?.accessToken &&
      !isRefreshing &&
      getTokenExpiryMs(tokens.accessToken) - Date.now() < PROACTIVE_THRESHOLD_MS
    ) {
      if (!proactiveRefreshPromise) {
        proactiveRefreshPromise = (async () => {
          isRefreshing = true;
          try {
            const response = await axios.post<RefreshTokenResponse>(
              `${API_BASE_URL}/auth/refresh`,
              {},                          // body empty — refreshToken comes from httpOnly cookie
              { withCredentials: true, timeout: 10_000 }
            );
            const { access_token } = response.data;
            setTokens({ accessToken: access_token });
            onTokenRefreshed(access_token);
          } catch {
            // Silent failure — the 401 response interceptor is the safety net
          } finally {
            isRefreshing = false;
            proactiveRefreshPromise = null;
          }
        })();
      }
      await proactiveRefreshPromise;
    }

    // Re-read tokens after possible proactive refresh.
    // Skip if the caller already set a custom Authorization header (e.g. SSO login).
    const freshTokens = useAuthStore.getState().tokens;
    if (freshTokens?.accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${freshTokens.accessToken}`;
    }

    // Add language header
    const language = getBackendLanguage();
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors and token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _skipLogout?: boolean;
    };

    // Handle 401 Unauthorized - Try to refresh using the httpOnly cookie
    // Skip logout behavior for requests marked with _skipLogout (non-critical operations)
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._skipLogout) {
      const { setTokens } = useAuthStore.getState();

      // If already refreshing, wait for new token
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // The httpOnly cookie carries the refreshToken automatically via withCredentials.
        const response = await axios.post<RefreshTokenResponse>(
          `${API_BASE_URL}/auth/refresh`,
          {},                          // body empty — refreshToken comes from httpOnly cookie
          { withCredentials: true, timeout: 10_000 }
        );

        const { access_token } = response.data;

        // Store only the new accessToken — refreshToken stays in cookie
        setTokens({ accessToken: access_token });

        // Sync brand config if present in refresh response
        if ('brandConfig' in response.data) {
          const [{ useBrandStore }, { profileResponseToBrandConfig }] = await Promise.all([
            import('@/features/b2b/store/brand.store'),
            import('@/features/b2b/utils/profileBrand'),
          ]);
          const brandStore = useBrandStore.getState();
          const currentUserId = useAuthStore.getState().user?.uuid ?? null;
          const brandConfig =
            profileResponseToBrandConfig(response.data.brandConfig) ?? null;
          if (currentUserId) {
            brandStore.setActiveUser(currentUserId);
            brandStore.setBrandConfigForUser(currentUserId, brandConfig);
          } else {
            brandStore.setBrandConfig(brandConfig);
          }
        }

        // Notify subscribers
        onTokenRefreshed(access_token);
        isRefreshing = false;

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed (cookie expired or invalid) — clear local state directly.
        // Do NOT call logout() here because it triggers revokeToken() which uses
        // apiClient → 401 → refresh fail → logout() → infinite loop.
        isRefreshing = false;
        refreshSubscribers = [];
        useAuthStore.setState({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          isSessionBootstrapping: false,
          error: null,
        });
        import('@/features/b2b/store/brand.store').then(m =>
          m.useBrandStore.getState().clearBrand()
        );
        router.navigate(ROUTES.AUTH.LOGIN, { replace: true });
        return Promise.reject(refreshError);
      }
    }

    // Transform error to consistent format
    const apiError: ApiError = {
      statusCode: error.response?.status || 500,
      message: error.response?.data?.message || 'An unexpected error occurred',
      error: error.response?.data?.error,
      errors: error.response?.data?.errors,
    };

    return Promise.reject(apiError);
  }
);

/**
 * Generic GET request
 */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(url, { params });
  return response.data;
}

/**
 * Generic POST request
 */
export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<T>(url, data);
  return response.data;
}

/**
 * Generic PUT request
 */
export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data);
  return response.data;
}

/**
 * Generic PATCH request
 */
export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
}

/**
 * Generic DELETE request
 */
export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url);
  return response.data;
}

/**
 * Upload file with FormData
 */
export async function upload<T>(
  url: string,
  file: File,
  fieldName = 'file',
  additionalData?: Record<string, string>
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await apiClient.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

export default apiClient;
